/**
 * Stakeholder domain type (Sprint 4.3.8).
 *
 * Source anchors:
 *   - Awell domain model file 03. Vault `03-knowledge-base/external-
 *     references/awell/orchestration/api-reference/overview/domain-model.md`.
 *     Quoted in the sprint brief: "A patient refers to a user that can be
 *     enrolled in a care flow. Activities in those care flows can be
 *     assigned to that patient but also other stakeholders: physician,
 *     nurse, care coordinator, therapist, coach, you name it." The
 *     `indirect_object` field on each activity carries the assignee
 *     identity at runtime.
 *   - Phase B canon. Vault `03-knowledge-base/domains/clinical/
 *     pathway-engineering/stakeholder-model.md`. Adds the Scrips-distinct
 *     `scrips-role-tier` attribute (clinical-decision / supervisory /
 *     read-only) and a separation-of-duty rule enforced at enrollment.
 *
 * The canonical Awell stakeholder JSON shape lives in
 * `src/services/care-flow-components/types.ts` (`StakeholderComponentJson`)
 * with role enum `patient | practitioner | caregiver | system` per the
 * Awell source-control vocabulary. This module models the runtime / UI
 * layer used by the stakeholder picker; the Scrips role-tier extension
 * is decoupled from the Awell baseline so the JSON serializer stays
 * source-anchored.
 *
 * HC1 reproducibility: `id` is a stable slug; never random.
 * HC5: zero em / en dashes.
 * HC9 strict registry: `STAKEHOLDER_ROLES` and `SCRIPS_ROLE_TIERS` are
 * closed tuples consumed by Zod; mutations always validate.
 */
import { z } from 'zod';

/**
 * Closed stakeholder role vocabulary. Anchored in the Awell domain model
 * file 03 enumeration plus the canonical Phase B examples in
 * `stakeholder-model.md`. Order matches the brief.
 */
export const STAKEHOLDER_ROLES = [
  'patient',
  'physician',
  'nurse',
  'care-coordinator',
  'therapist',
  'coach',
  'pharmacist',
  'social-worker',
  'other',
] as const;

export type StakeholderRole = (typeof STAKEHOLDER_ROLES)[number];

/**
 * Scrips-distinct authorization tier. Per Phase B
 * `stakeholder-model.md`, every stakeholder is assigned to one of three
 * tiers. The tier governs which interface surface the stakeholder's
 * activities appear on AND drives the separation-of-duty constraint at
 * enrollment time (no person bound to both `clinical-decision` and
 * `supervisory` on the same enrollment).
 */
export const SCRIPS_ROLE_TIERS = [
  'clinical-decision',
  'supervisory',
  'read-only',
] as const;

export type ScripsRoleTier = (typeof SCRIPS_ROLE_TIERS)[number];

export interface Stakeholder {
  /** Stable slug-cased id (deterministic; never random). */
  id: string;
  /** Display name surfaced in the picker + the inspector chip. */
  name: string;
  role: StakeholderRole;
  /** Scrips-distinct authorization tier. */
  tier: ScripsRoleTier;
  /** Optional free-form description for the picker tooltip. */
  description?: string;
  /**
   * Separation-of-duty hint: this stakeholder cannot also act as one of
   * the listed roles on the same patient enrollment. Anchored in Phase B
   * `stakeholder-model.md` (a `care-team` person cannot also be the
   * `clinical-supervisor`).
   */
  incompatibleWith?: ReadonlyArray<StakeholderRole>;
}

/** Zod schema for the Stakeholder type. Rejects unknown roles + tiers. */
export const stakeholderSchema = z.object({
  id: z.string().min(1, 'id is required'),
  name: z.string().min(1, 'name is required').max(120),
  role: z.enum(STAKEHOLDER_ROLES),
  tier: z.enum(SCRIPS_ROLE_TIERS),
  description: z.string().max(280).optional(),
  incompatibleWith: z.array(z.enum(STAKEHOLDER_ROLES)).optional(),
});

/**
 * Human-readable label lookup for stakeholder roles. Used by the picker
 * + dialog to render the dropdown labels without ad-hoc string casing.
 */
export const STAKEHOLDER_ROLE_LABELS: Record<StakeholderRole, string> = {
  patient: 'Patient',
  physician: 'Physician',
  nurse: 'Nurse',
  'care-coordinator': 'Care coordinator',
  therapist: 'Therapist',
  coach: 'Coach',
  pharmacist: 'Pharmacist',
  'social-worker': 'Social worker',
  other: 'Other',
};

/** Human-readable tier labels. */
export const SCRIPS_ROLE_TIER_LABELS: Record<ScripsRoleTier, string> = {
  'clinical-decision': 'Clinical decision',
  supervisory: 'Supervisory',
  'read-only': 'Read only',
};

/**
 * Slugify a free-form name into a stakeholder id. Deterministic per HC1.
 * Lowercase ASCII, hyphen-joined, non-alphanumeric collapsed.
 */
export function slugifyStakeholderName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
